/**
 * Competition Manager certification quiz — 8 timed interactive questions.
 * Tests setup skills: divisions, heats, rider assignment, invitations.
 */

import { useState } from "react";
import { QuizEngine, type QuizQuestion } from "./QuizEngine";
import { MockSetupScreen, type MockSetupRider } from "./MockSetupScreen";

const RIDERS: MockSetupRider[] = [
  { id: "r1", name: "Harley Clifford", nickname: "The King", level: "Pro" },
  { id: "r2", name: "Nic Rapa", nickname: "Raptor", level: "Pro" },
  { id: "r3", name: "Steel Lafferty", nickname: "Steele", level: "Pro" },
  { id: "r4", name: "Dallas Friday", nickname: "D-Fri", level: "Open" },
  { id: "r5", name: "Amber Wing", nickname: "Wings", level: "Open" },
  { id: "r6", name: "Jake Thompson", nickname: "JT", level: "Juniors" },
  { id: "r7", name: "Mia Rodriguez", nickname: "Mimi", level: "Juniors" },
];

// ─── Q1: Create a division ──────────────────────────────────

function Q1Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  return (
    <MockSetupScreen
      initialDivisions={[]}
      initialHeats={[]}
      availableRiders={RIDERS}
      onDivisionCreate={(name) => {
        if (name.toLowerCase().includes("pro")) onComplete(true);
      }}
    />
  );
}

// ─── Q2: Create a heat in a division ────────────────────────

function Q2Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  return (
    <MockSetupScreen
      initialDivisions={[
        { id: "d1", name: "Men's Pro" },
        { id: "d2", name: "Women's Open" },
      ]}
      initialHeats={[]}
      availableRiders={RIDERS}
      onHeatCreate={(name, divisionId) => {
        if (divisionId === "d1" && name.toLowerCase().includes("final")) onComplete(true);
      }}
    />
  );
}

// ─── Q3: Add riders to a heat ───────────────────────────────

function Q3Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  const [added, setAdded] = useState<Set<string>>(new Set());

  return (
    <MockSetupScreen
      initialDivisions={[{ id: "d1", name: "Men's Pro" }]}
      initialHeats={[{ id: "h1", name: "Finals", divisionId: "d1", maxAttempts: 3, riders: [] }]}
      availableRiders={RIDERS}
      onRiderAdded={(_heatId, riderId) => {
        const next = new Set(added);
        next.add(riderId);
        setAdded(next);
        // Need Harley + Steel (2 pro riders)
        if (next.has("r1") && next.has("r3")) onComplete(true);
      }}
    />
  );
}

// ─── Q4: Invite a judge ─────────────────────────────────────

function Q4Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  return (
    <MockSetupScreen
      initialDivisions={[{ id: "d1", name: "Men's Pro" }]}
      initialHeats={[{ id: "h1", name: "Finals", divisionId: "d1", maxAttempts: 3, riders: [
        { id: "r1", name: "Harley Clifford", nickname: "The King" },
        { id: "r3", name: "Steel Lafferty", nickname: "Steele" },
      ] }]}
      availableRiders={RIDERS}
      onInviteSent={(_email, role) => {
        if (role === "judge") onComplete(true);
      }}
    />
  );
}

// ─── Q5: Invite a producer ──────────────────────────────────

function Q5Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  return (
    <MockSetupScreen
      initialDivisions={[{ id: "d1", name: "Men's Pro" }]}
      initialHeats={[{ id: "h1", name: "Finals", divisionId: "d1", maxAttempts: 3, riders: [
        { id: "r1", name: "Harley Clifford" },
      ] }]}
      availableRiders={RIDERS}
      onInviteSent={(_email, role) => {
        if (role === "producer") onComplete(true);
      }}
    />
  );
}

// ─── Q6: Create multiple divisions + heats ──────────────────

function Q6Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  const [divs, setDivs] = useState(0);
  const [heatsCreated, setHeatsCreated] = useState(0);

  return (
    <MockSetupScreen
      initialDivisions={[]}
      initialHeats={[]}
      availableRiders={RIDERS}
      onDivisionCreate={() => {
        setDivs((n) => {
          const next = n + 1;
          if (next >= 2 && heatsCreated >= 2) onComplete(true);
          return next;
        });
      }}
      onHeatCreate={() => {
        setHeatsCreated((n) => {
          const next = n + 1;
          if (divs >= 2 && next >= 2) onComplete(true);
          return next;
        });
      }}
    />
  );
}

// ─── Q7: Full setup — division, heat, riders, invite ────────

function Q7Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  const [step, setStep] = useState(0);
  const steps = [
    "Create a division called 'Juniors'",
    "Create a heat called 'Round 1' in Juniors",
    "Add Jake Thompson to Round 1",
    "Invite a judge",
  ];

  return (
    <div>
      <div style={{
        display: "flex", gap: 8, marginBottom: 12, padding: "8px 12px",
        background: "var(--c-surface-2)", border: "1px solid var(--c-border)", flexWrap: "wrap",
      }}>
        {steps.map((s, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 6, fontSize: 11,
            color: i < step ? "#22c55e" : i === step ? "var(--c-text)" : "var(--c-text-dim)",
            fontWeight: i === step ? 700 : 400,
          }}>
            <span style={{
              width: 18, height: 18, borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 9, fontWeight: 700,
              background: i < step ? "#22c55e" : i === step ? "var(--c-accent)" : "var(--c-surface-2)",
              color: i <= step ? "#fff" : "var(--c-text-dim)",
              border: `1px solid ${i < step ? "#22c55e" : i === step ? "var(--c-accent)" : "var(--c-border)"}`,
            }}>
              {i < step ? "\u2713" : i + 1}
            </span>
            {s}
          </div>
        ))}
      </div>

      <MockSetupScreen
        initialDivisions={[]}
        initialHeats={[]}
        availableRiders={RIDERS}
        onDivisionCreate={(name) => {
          if (step === 0 && name.toLowerCase().includes("junior")) setStep(1);
        }}
        onHeatCreate={(name) => {
          if (step === 1 && name.toLowerCase().includes("round")) setStep(2);
        }}
        onRiderAdded={(_heatId, riderId) => {
          if (step === 2 && riderId === "r6") setStep(3);
        }}
        onInviteSent={(_email, role) => {
          if (step === 3 && role === "judge") onComplete(true);
        }}
      />
    </div>
  );
}

// ─── Q8: Speed round — create 2 divisions, 3 heats, 4 riders

function Q8Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  const [divCount, setDivCount] = useState(0);
  const [heatCount, setHeatCount] = useState(0);
  const [riderCount, setRiderCount] = useState(0);

  const needed = { divs: 2, heats: 3, riders: 4 };

  return (
    <div>
      <div style={{
        display: "flex", gap: 16, marginBottom: 12, padding: "8px 12px",
        background: "var(--c-surface-2)", border: "1px solid var(--c-border)",
      }}>
        <span style={{ fontSize: 12, color: divCount >= needed.divs ? "#22c55e" : "var(--c-text)" }}>
          Divisions: {divCount}/{needed.divs} {divCount >= needed.divs ? "\u2713" : ""}
        </span>
        <span style={{ fontSize: 12, color: heatCount >= needed.heats ? "#22c55e" : "var(--c-text)" }}>
          Heats: {heatCount}/{needed.heats} {heatCount >= needed.heats ? "\u2713" : ""}
        </span>
        <span style={{ fontSize: 12, color: riderCount >= needed.riders ? "#22c55e" : "var(--c-text)" }}>
          Riders assigned: {riderCount}/{needed.riders} {riderCount >= needed.riders ? "\u2713" : ""}
        </span>
      </div>

      <MockSetupScreen
        initialDivisions={[]}
        initialHeats={[]}
        availableRiders={RIDERS}
        onDivisionCreate={() => {
          setDivCount((n) => n + 1);
        }}
        onHeatCreate={() => {
          setHeatCount((n) => n + 1);
        }}
        onRiderAdded={() => {
          setRiderCount((n) => {
            const next = n + 1;
            if (divCount + 1 >= needed.divs && heatCount + 1 >= needed.heats && next >= needed.riders) {
              // Small delay to let UI update
              setTimeout(() => onComplete(true), 100);
            }
            return next;
          });
        }}
      />
    </div>
  );
}

// ─── Quiz definition ────────────────────────────────────────

const CM_QUESTIONS: QuizQuestion[] = [
  {
    id: "create-division",
    title: "Create a Division",
    instruction: "Create a division with 'Pro' in the name",
    timeLimit: 10,
    render: (props) => <Q1Render {...props} />,
  },
  {
    id: "create-heat",
    title: "Create a Heat",
    instruction: "Create a heat called 'Finals' in Men's Pro",
    timeLimit: 12,
    render: (props) => <Q2Render {...props} />,
  },
  {
    id: "add-riders",
    title: "Add Riders to Heat",
    instruction: "Add Harley Clifford and Steel Lafferty to the Finals heat",
    timeLimit: 15,
    render: (props) => <Q3Render {...props} />,
  },
  {
    id: "invite-judge",
    title: "Invite a Judge",
    instruction: "Invite someone as a judge (any email)",
    timeLimit: 12,
    render: (props) => <Q4Render {...props} />,
  },
  {
    id: "invite-producer",
    title: "Invite a Producer",
    instruction: "Invite someone as a producer (any email)",
    timeLimit: 12,
    render: (props) => <Q5Render {...props} />,
  },
  {
    id: "multi-setup",
    title: "Multiple Divisions & Heats",
    instruction: "Create 2 divisions and at least 2 heats",
    timeLimit: 25,
    render: (props) => <Q6Render {...props} />,
  },
  {
    id: "full-setup",
    title: "Full Setup Sequence",
    instruction: "Create 'Juniors' division → 'Round 1' heat → Add Jake Thompson → Invite a judge",
    timeLimit: 35,
    render: (props) => <Q7Render {...props} />,
  },
  {
    id: "speed-setup",
    title: "Speed Setup",
    instruction: "Create 2 divisions, 3 heats, and assign 4 riders total",
    timeLimit: 45,
    render: (props) => <Q8Render {...props} />,
  },
];

// ─── Export ─────────────────────────────────────────────────

interface CompetitionManagerQuizProps {
  onPass: () => void;
  onClose: () => void;
}

export function CompetitionManagerQuiz({ onPass, onClose }: CompetitionManagerQuizProps) {
  return (
    <QuizEngine
      title="Competition Manager Certification Quiz"
      questions={CM_QUESTIONS}
      onPass={onPass}
      onClose={onClose}
    />
  );
}
